// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

export const VALID_METRIC_TYPES = [
  // Technical Influence Metrics (5)
  'maintainers',
  'contributors',
  'commit_activities',
  'pull_requests_opened',
  'avg_time_to_merge_prs',
  // Ecosystem Influence Metrics (9)
  'board_members',
  'committee_members',
  'meeting_attendance',
  'event_attendance',
  'event_speakers',
  'event_sponsorships',
  'meetup_attendance',
  'collaboration_activity',
  'certified_individuals',
] as const;

export type MetricType = typeof VALID_METRIC_TYPES[number];

const METRIC_DISPLAY_NAMES: Record<MetricType, string> = {
  // Technical Influence
  maintainers: 'Maintainers',
  contributors: 'Contributors',
  commit_activities: 'Commit Activities',
  pull_requests_opened: 'Pull Requests Opened',
  avg_time_to_merge_prs: 'Average Time to Merge PRs',
  // Ecosystem Influence
  board_members: 'Board Members',
  committee_members: 'Committee Members',
  meeting_attendance: 'Meeting Attendance',
  event_attendance: 'Event Attendance',
  event_speakers: 'Event Speakers',
  event_sponsorships: 'Event Sponsorships',
  meetup_attendance: 'Meetup Attendance',
  collaboration_activity: 'Collaboration Activity',
  certified_individuals: 'Certified Individuals',
};

export interface ReportIssueRequest {
  area: string;
  description: string;
  url?: string;
}

export interface ReportIssueResponse {
  success: boolean;
  message: string;
  issueKey?: string;
  issueId?: string;
  webUrl?: string;
}

export interface ReportIssueError {
  success: false;
  error: string;
  message: string;
  details?: any;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface ReportIssueConfig {
  display: {
    title: string;
    confirmLabel: string;
    successMessage: string;
    errorMessage: string;
    toastHeader: string;
    toastMessage: string;
    viewIssueLinkText: string;
  };
  validation: {
    requiredFields: (keyof ReportIssueRequest)[];
    minDescriptionLength: number;
    maxDescriptionLength: number;
  };
  fields: {
    area: {
      label: string;
      helpText: string;
    };
    description: {
      label: string;
      helpText: string;
    };
  };
}

export class ReportIssueEntity {
  protected static readonly CONFIG: ReportIssueConfig = {
    display: {
      title: 'Report Issue',
      confirmLabel: 'Report issue',
      successMessage: 'Thank you! Your report has been submitted successfully.',
      errorMessage: 'Failed to report issue. Please try again.',
      toastHeader: 'Issue successfully reported',
      toastMessage: `Thanks for your feedback! We'll look into the issue shortly.`,
      viewIssueLinkText: 'View issue',
    },
    validation: {
      requiredFields: ['area', 'description'],
      minDescriptionLength: 10,
      maxDescriptionLength: 5000,
    },
    fields: {
      area: {
        label: 'Select area',
        helpText: 'Choose the area where you encountered the issue.',
      },
      description: {
        label: 'Description',
        helpText: 'Clear and concise description of the issue.',
      },
    },
  };

  static getConfig(): ReportIssueConfig {
    return this.CONFIG;
  }

  static validate(report: Partial<ReportIssueRequest>): ValidationResult {
    const errors: string[] = [];

    if (!report.area?.trim()) {
      errors.push('Metric type is required');
    } else if (!this.isValidMetricType(report.area)) {
      errors.push(`Invalid metric type: ${report.area}`);
    }

    const trimmedDescription = report.description?.trim() || '';
    if (!trimmedDescription) {
      errors.push('Description is required');
    } else if (trimmedDescription.length < this.CONFIG.validation.minDescriptionLength) {
      errors.push(
        `Description must be at least ${this.CONFIG.validation.minDescriptionLength} characters`
      );
    } else if (trimmedDescription.length > this.CONFIG.validation.maxDescriptionLength) {
      errors.push(
        `Description cannot exceed ${this.CONFIG.validation.maxDescriptionLength} characters`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  static isValidMetricType(metricType: string): boolean {
    return VALID_METRIC_TYPES.includes(metricType as MetricType);
  }

  static getMetricDisplayName(metricType: string): string {
    if (!this.isValidMetricType(metricType)) {
      return metricType;
    }
    return METRIC_DISPLAY_NAMES[metricType as MetricType];
  }

  static toApiPayload(report: ReportIssueRequest): ReportIssueRequest {
    return {
      area: report.area.trim(),
      description: report.description.trim(),
      url: report.url,
    };
  }

  static createEmpty(metricType: string): Partial<ReportIssueRequest> {
    return {
      area: metricType,
      description: '',
      url: undefined,
    };
  }

  static getValidMetricTypes(): readonly string[] {
    return VALID_METRIC_TYPES;
  }
}

